// Hook que encapsula o estado server-side do caixa:
//   - movimentos do caixa atual
//   - resumo por forma de pagamento
//   - historico de todos os caixas (abertos e fechados)
//
// (Os pedidos do caixa saíram daqui na v1.5.784: a tela manda para Pedidos
// filtrado pelo caixa, e o fechamento busca os dele.)
// Carrega tudo em paralelo. Expoe um `refresh` para reload manual apos
// mutacoes (sangria, suprimento, reconciliacao, fechamento).
//
// Carregamento com cards (25/10/2026): `esperando` fica ligado até o resumo do
// caixa de agora chegar — sem ele a aba mostrava R$ 0,00 e "Nenhuma venda"
// antes da hora. O caixa aberto vem do cabeçalho do painel (`caixaAberto`), que
// começa `null` até a consulta dele voltar; por isso, na primeira carga sem
// caixa, a tela pergunta o status ela mesma (`aoDescobrirAberto`) em vez de
// mostrar "Caixa fechado" para um caixa que está aberto.

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { caixaApi } from "../api/caixaApi";

export function useCaixaData(caixaRecebido, aoDescobrirAberto) {
  // Caixa sem id não é caixa aberto (resposta de erro que virou objeto): sem
  // isto a tela ficava no esqueleto para sempre esperando o resumo de "undefined"
  const caixaAberto = caixaRecebido?.id ? caixaRecebido : null;
  const [movimentos, setMovimentos] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);
  // id do caixa cujos dados já chegaram (null = sem caixa aberto; undefined = nada ainda)
  const [carregadoPara, setCarregadoPara] = useState(undefined);
  const primeira = useRef(true);
  const ultima = useRef(0);

  const refresh = useCallback(async () => {
    const vez = ++ultima.current;
    const perguntarStatus = !caixaAberto && primeira.current;
    primeira.current = false;
    setLoading(true);
    try {
      const [movRes, resumoRes, histRes, status] = await Promise.all([
        caixaAberto ? caixaApi.listarMovimentos(caixaAberto.id) : Promise.resolve([]),
        caixaAberto ? caixaApi.obterResumo(caixaAberto.id) : Promise.resolve(null),
        caixaApi.listarHistorico(),
        perguntarStatus ? caixaApi.status().catch(() => null) : Promise.resolve(null),
      ]);
      if (vez !== ultima.current) return; // chegou uma resposta mais nova
      setMovimentos(movRes);
      setResumo(resumoRes);
      setHistorico(histRes);
      if (status && status.status !== "fechado" && status.id && aoDescobrirAberto) {
        // Caixa aberto que o cabeçalho ainda não sabia: segue no esqueleto até
        // o resumo dele chegar (o refresh roda de novo com o caixa).
        aoDescobrirAberto(status);
        return;
      }
      setCarregadoPara(caixaAberto ? caixaAberto.id : null);
    } catch (e) {
      if (vez !== ultima.current) return;
      console.error("[useCaixaData] erro:", e);
      toast.error("Erro ao carregar dados do caixa");
      setCarregadoPara(caixaAberto ? caixaAberto.id : null);
    } finally {
      if (vez === ultima.current) setLoading(false);
    }
  }, [caixaAberto]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refresh();
  }, [refresh]);

  const esperando = carregadoPara === undefined || (!!caixaAberto && carregadoPara !== caixaAberto.id);
  return { movimentos, resumo, historico, loading, esperando, refresh };
}
